import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";

/**
 * Pharmacy Search API Service
 * Provides advanced search functionality for POS, Prescriptions, and Free Medicine
 */
class PharmacySearchService {
  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use(
      (config) => {
        const user = localStorage.getItem("user");
        if (user) {
          const userData = JSON.parse(user);
          if (userData.token) {
            config.headers.Authorization = `Token ${userData.token}`;
          }
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Advanced search across all pharmacy components
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.type - Search type: 'all', 'pos', 'prescription', 'free_medicine'
   * @param {string} searchParams.search - Search query
   * @param {number} searchParams.page - Page number
   * @param {number} searchParams.page_size - Items per page
   * @param {string} searchParams.payment_status - Payment status filter
   * @param {string} searchParams.date_from - Start date filter
   * @param {string} searchParams.date_to - End date filter
   * @param {string} searchParams.patient_id - Patient ID filter
   * @param {string} searchParams.doctor_id - Doctor ID filter
   * @returns {Promise<Object>} Search results
   */
  async searchPharmacy(searchParams = {}) {
    try {
      const params = new URLSearchParams();

      // Add all search parameters
      Object.keys(searchParams).forEach((key) => {
        if (
          searchParams[key] !== undefined &&
          searchParams[key] !== null &&
          searchParams[key] !== ""
        ) {
          params.append(key, searchParams[key]);
        }
      });

      const response = await this.api.get(
        `/pharmacy/search/?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error("Pharmacy search error:", error);
      throw error;
    }
  }

  /**
   * Quick search for autocomplete and suggestions
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of suggestions
   * @returns {Promise<Object>} Suggestions
   */
  async quickSearch(query, limit = 10) {
    try {
      const response = await this.api.get("/pharmacy/quick-search/", {
        params: { q: query, limit },
      });
      return response.data;
    } catch (error) {
      console.error("Quick search error:", error);
      throw error;
    }
  }

  /**
   * Search POS orders specifically
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Object>} POS search results
   */
  async searchPOSOrders(searchParams = {}) {
    return this.searchPharmacy({ ...searchParams, type: "pos" });
  }

  /**
   * Search prescriptions specifically
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Object>} Prescription search results
   */
  async searchPrescriptions(searchParams = {}) {
    return this.searchPharmacy({ ...searchParams, type: "prescription" });
  }

  /**
   * Search free medicine orders specifically
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Object>} Free medicine search results
   */
  async searchFreeMedicineOrders(searchParams = {}) {
    return this.searchPharmacy({ ...searchParams, type: "free_medicine" });
  }

  /**
   * Search with filters and pagination
   * @param {Object} filters - Filter parameters
   * @param {Object} pagination - Pagination parameters
   * @returns {Promise<Object>} Filtered search results
   */
  async searchWithFilters(filters = {}, pagination = {}) {
    const searchParams = {
      ...filters,
      page: pagination.page || 1,
      page_size: pagination.pageSize || 10,
    };

    return this.searchPharmacy(searchParams);
  }

  /**
   * Get search suggestions for a specific type
   * @param {string} type - Suggestion type: 'patient', 'doctor', 'medicine'
   * @param {string} query - Search query
   * @returns {Promise<Array>} Filtered suggestions
   */
  async getSuggestionsByType(type, query) {
    try {
      const suggestions = await this.quickSearch(query);
      return suggestions.suggestions.filter(
        (suggestion) => suggestion.type === type
      );
    } catch (error) {
      console.error("Get suggestions error:", error);
      throw error;
    }
  }

  /**
   * Search by date range
   * @param {string} dateFrom - Start date (YYYY-MM-DD)
   * @param {string} dateTo - End date (YYYY-MM-DD)
   * @param {string} type - Search type
   * @returns {Promise<Object>} Date range search results
   */
  async searchByDateRange(dateFrom, dateTo, type = "all") {
    return this.searchPharmacy({
      type,
      date_from: dateFrom,
      date_to: dateTo,
    });
  }

  /**
   * Search by payment status
   * @param {string} paymentStatus - Payment status (1: Pending, 2: Paid, 3: Canceled)
   * @param {string} type - Search type
   * @returns {Promise<Object>} Payment status search results
   */
  async searchByPaymentStatus(paymentStatus, type = "all") {
    return this.searchPharmacy({
      type,
      payment_status: paymentStatus,
    });
  }

  /**
   * Search by patient
   * @param {string} patientId - Patient ID
   * @param {string} type - Search type
   * @returns {Promise<Object>} Patient search results
   */
  async searchByPatient(patientId, type = "all") {
    return this.searchPharmacy({
      type,
      patient_id: patientId,
    });
  }

  /**
   * Search by doctor
   * @param {string} doctorId - Doctor ID
   * @returns {Promise<Object>} Doctor search results
   */
  async searchByDoctor(doctorId) {
    return this.searchPharmacy({
      type: "prescription",
      doctor_id: doctorId,
    });
  }

  /**
   * Get comprehensive search results with all filters
   * @param {Object} searchConfig - Complete search configuration
   * @returns {Promise<Object>} Comprehensive search results
   */
  async comprehensiveSearch(searchConfig = {}) {
    const {
      query = "",
      type = "all",
      filters = {},
      pagination = {},
      sortBy = "id",
      sortOrder = "desc",
    } = searchConfig;

    const searchParams = {
      search: query,
      type,
      ...filters,
      page: pagination.page || 1,
      page_size: pagination.pageSize || 10,
    };

    try {
      const results = await this.searchPharmacy(searchParams);

      // Add sorting if needed (this would be handled by backend in a real implementation)
      if (sortBy && sortOrder) {
        // Frontend sorting logic could be added here if needed
        console.log("Sorting by:", sortBy, sortOrder);
      }

      return results;
    } catch (error) {
      console.error("Comprehensive search error:", error);
      throw error;
    }
  }
}

// Create and export a singleton instance
const pharmacySearchService = new PharmacySearchService();
export default pharmacySearchService;

// Export the class for custom instances
export { PharmacySearchService };
